import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'

import { Spinner } from 'components/common'
import { User } from 'types/user'
import { verifyToken } from 'utils/api/auth'

type Props = {
  children: React.ReactNode
}

type AuthContextType = {
  user: User
  isAuthenticated: boolean
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>
  /**
   * Call this immediately after a successful login (post-OTP verify) to
   * hydrate the context before navigation. This prevents the brief flash
   * of the login / unauthorized page that occurs when the route changes
   * and AuthProvider hasn't finished re-fetching yet.
   */
  loginUser: (user: User) => void
}

export const AuthContext = createContext<undefined | AuthContextType>(undefined)

const AuthProvider: React.FC<Props> = ({ children }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const [user, setUser] = useState<User | any>({})
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)

  // Only block rendering (show spinner) on the very first fetch.
  // Subsequent re-fetches on pathname changes run silently in the background
  // so there is no flash to the login page mid-session.
  const [initialLoading, setInitialLoading] = useState<boolean>(true)
  const isFirstFetch = useRef(true)

  const fetchUser = async () => {
    try {
      const { data } = await verifyToken()
      if (data?.user && data?.user?.isActive) {
        setUser(data.user)
        setIsAuthenticated(true)
      } else {
        setUser({})
        setIsAuthenticated(false)
      }
    } catch (error) {
      if (
        error instanceof AxiosError &&
        !error.response &&
        error.request &&
        pathname !== '/server-error'
      ) {
        navigate('/server-error')
        return
      }
      setUser({})
      setIsAuthenticated(false)
    } finally {
      if (isFirstFetch.current) {
        setInitialLoading(false)
        isFirstFetch.current = false
      }
    }
  }

  // Immediately hydrates the context after a successful login so the
  // protected route sees isAuthenticated === true before navigation completes.
  const loginUser = (loggedInUser: User) => {
    setUser(loggedInUser)
    setIsAuthenticated(true)
    // Mark initial loading done in case loginUser is called before
    // fetchUser resolves (e.g. very fast network).
    if (isFirstFetch.current) {
      setInitialLoading(false)
      isFirstFetch.current = false
    }
  }

  useEffect(() => {
    fetchUser()
  }, [pathname])

  const value: AuthContextType = useMemo(
    () => ({
      user,
      isAuthenticated,
      setIsAuthenticated,
      loginUser,
    }),
    [user, isAuthenticated],
  )

  if (initialLoading) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center">
        <Spinner size="large" />
      </div>
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider
